import Controller, { OnBeforeSubmit, OnClick, OnMount, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, FormAPI, InputAPI, ModalProps, ModalService, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import BaseController from '../../../BaseController';
import { FragmentNames, ScoreDiniEditAliases } from '../../../../codegen/index';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import timeService from '../../../../services/time-service';
import { Inject} from '@odinjs/odin';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import {DomainType, ResponseCreateScoreDINI, ScoreDINIResponse} from '../../score-types';
import { ApplicationRoute } from '../../../../constants/routes';
import DialogService from '../../../../services/dialog-service';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { WizardSteps } from '../../../../constants/wizards';

@Controller({ component: ScoreDiniEditAliases.SCORE_DINI_ADD_LAYOUT, fragment: FragmentNames.SCORE_DINI_EDIT })
export default class ScoreDiniEditController extends BaseController {

  @Inject(ScoreDiniEditAliases.INPUT_SETUP_DATE)
  private setupDateInputApi!: InputAPI;

  @Inject(ScoreDiniEditAliases.FORM_ONE)
  private formOneScoreDiniCreateAPI!: FormAPI;

  @Inject(ScoreDiniEditAliases.FORM_TWO)
  private formTwoScoreDiniCreateAPI!: FormAPI;

  @Inject(ScoreDiniEditAliases.WIZARD)
  private wizard!: WizardAPI;

  @Inject(ScoreDiniEditAliases.ACTION_DELETE_SCORE_DINI)
  private actionDeleteAPI!: ActionAPI;

  @Inject(ScoreDiniEditAliases.ACTION_NEW_SCORE_DINI)
  private actionNewAPI!: ActionAPI;

  @Inject(ScoreDiniEditAliases.ACTION_REFERENCE_SCORE_DINI)
  private actionReferenceAPI!: ActionAPI;

  @Inject(ScoreDiniEditAliases.ACTION_RELEASE_SCORE_DINI)
  private actionReleaseAPI!: ActionAPI;

  @OnValidate(ScoreDiniEditAliases.FORM_ONE)
  handleOnValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_ONE, validation.valid);
  }

  @OnValidate(ScoreDiniEditAliases.FORM_TWO)
  handleOnSecondValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_TWO, validation.valid);
  }

  @OnMount({ target: ScoreDiniEditAliases.FORM_ONE })
  handleMountForm(): void {
    this.formOneScoreDiniCreateAPI.setLoading(true);
    applicationContext.popNavigationItem();
    this.actionDeleteAPI.setEnabled(false);
    this.actionNewAPI.setEnabled(false);
    this.actionReferenceAPI.setEnabled(false);
    this.actionReleaseAPI.setEnabled(false);
  }

  @OnUnmount({ target: ScoreDiniEditAliases.FORM_ONE })
  handleUnMountForm(): void {
    this.clearAbortController();
  }

  @OnMount({ target: ScoreDiniEditAliases.WIZARD })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_DINI
    const { encounterId, patientId, idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.loadEditionData(idScoreIndexes as number);
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
  }

  @OnMount({target: ScoreDiniEditAliases.INPUT_SETUP_DATE})
  handleInputMount(): void {
    timeService.getCurrentDateTime(this.setAbortController())
      .then(date => this.setupDateInputApi.setValue(date))
      .catch(() => this.setupDateInputApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-dini-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private releaseDINIScore(sequenceNumber: number): void {
    this.formOneScoreDiniCreateAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.put(Endpoint.RELEASE_SCORE_DINI, { sequenceNumber }, { abortController })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formOneScoreDiniCreateAPI.setLoading(false);
      });
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseDINIScore(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private createNewDINIScore(body: Record<string, Value>): void {
    this.formTwoScoreDiniCreateAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.post(Endpoint.POST_CREATE_SCORE_DINI, undefined, { abortController, body })
      .then(response => {
        const {idScoreIndexes} = response.body as ResponseCreateScoreDINI;
        this.showModalSubmit(idScoreIndexes);
        this.context.set({ idScoreIndexes, isCreated: true });
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formTwoScoreDiniCreateAPI.setLoading(false));
  }

  private loadEditionData(sequenceNumber: number): void {
    const abortController = this.setAbortController();
    HttpService.get(Endpoint.SCORE_DINI, { sequenceNumber }, {abortController})
      .then(response => {
        const record = response.body as ScoreDINIResponse;

        this.formOneScoreDiniCreateAPI.setEditing(true);
        this.formOneScoreDiniCreateAPI.validate();
        this.formOneScoreDiniCreateAPI.setDisabled(true);
        const dateEvaluation = String(record.dateEvaluation);

        this.formOneScoreDiniCreateAPI.setRecord({...record, dateEvaluation});
        this.formTwoScoreDiniCreateAPI.setRecord(record as unknown as Record<string, ScoreDINIResponse>);

        this.wizard.setStepDone(WizardSteps.STEP_ONE, true);
        this.wizard.setStepDone(WizardSteps.STEP_TWO, true);
      }).catch((err) => this.showToast(err?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formOneScoreDiniCreateAPI.setLoading(false)
        this.actionDeleteAPI.setEnabled(true);
        this.actionNewAPI.setEnabled(true);
        this.actionReferenceAPI.setEnabled(true);
        this.actionReleaseAPI.setEnabled(true);
      });
  }

  @OnBeforeSubmit({ target: ScoreDiniEditAliases.WIZARD })
  handleBeforeSubmit(): void {
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    const isCreated = this.context.get('isCreated');
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formDataOne = this.formOneScoreDiniCreateAPI.getRecord();
    const formDataTwo = this.formTwoScoreDiniCreateAPI.getRecord();
    const mucosalCutaneousIntegrity = formDataOne.mucosalCutaneousIntegrity as DomainType;
    const networkSupport = formDataTwo.networkSupport as DomainType;
    const oxygenation = formDataOne.oxygenation as DomainType;
    const intervControles = formDataOne.intervControles as DomainType;
    const partCompanion = formDataTwo.partCompanion as DomainType;
    const activity = formDataOne.activity as DomainType;
    const bodyHygiene = formDataTwo.bodyHygiene as DomainType;
    const drugTherapy = formDataOne.drugTherapy as DomainType;
    const elimination = formDataTwo.eliminations as DomainType;
    const foodAndHydration = formDataTwo.foodAndHydration as DomainType;
    const mobility = formDataTwo.mobility as DomainType;
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const evaluationDate = moment(formDataOne.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT);

    const body = {
      activity: activity.valueDomain,
      attendance: encounterId as number,
      bodyHygiene: bodyHygiene.valueDomain,
      drugTherapy: drugTherapy.valueDomain,
      eliminations: elimination.valueDomain,
      evaluationDate,
      foodAndHydration: foodAndHydration.valueDomain,
      idScoreIndexes: idScoreIndexes as number,
      intervControles: intervControles.valueDomain,
      mobility: mobility.valueDomain,
      mucosalCutaneousIntegrity: mucosalCutaneousIntegrity.valueDomain,
      networkSupport: networkSupport.valueDomain,
      oxygenation: oxygenation.valueDomain,
      partCompanion: partCompanion.valueDomain,
      professionalCode: user.physicalPersonId,
    } as Record<string, Value>;
    this.createNewDINIScore(body);
  }

  private delete(sequenceNumber: number): void {
    this.formOneScoreDiniCreateAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.delete(Endpoint.SCORE_DINI, {
      sequenceNumber
    }, {abortController}).then(() => {
      applicationContext.popNavigationItem();
    }).catch((err) => {
      this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
    }).finally(() => {
      this.formOneScoreDiniCreateAPI.setLoading(false);
    });
  }

  private confirmDelete(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { applicationContext.popNavigationItem() },
      onClickOk: () => { this.delete(idScore) },
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  private getReference(): void {
    const scoreType = 'dini';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  @OnClick({ target: ScoreDiniEditAliases.ACTION_NEW_SCORE_DINI })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.SCORE_DINI_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick({ target: ScoreDiniEditAliases.ACTION_RELEASE_SCORE_DINI })
  handleClickRelease(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreDiniEditAliases.ACTION_DELETE_SCORE_DINI })
  handleClickDelete(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.confirmDelete(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreDiniEditAliases.ACTION_REFERENCE_SCORE_DINI })
  handleClickReference(): void {
    this.getReference();
  }

  @OnUnmount(ScoreDiniEditAliases.SCORE_DINI_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_DINI;
  }
}
